/* eslint-disable complexity */
import { IUser } from 'bg-server/src/components/api/v1/user/types';

import { ENV } from 'src/config/environments';
import { httpRequest } from 'src/helpers/http';
import { buildSearchRegex } from 'src/helpers/utils';
import { IProfesionalSearchFilter, IUserSearchFilter } from 'src/interfaces';

const getApiEndpoint = () => {
    const selectedCity = localStorage.getItem('selectedCity') || ENV.apiEndpoints[0].key;
    const apiEndpoint = ENV.apiEndpoints.find(endpoint => endpoint.key === selectedCity);
    return apiEndpoint ? `${apiEndpoint.url}/user` : `${ENV.apiEndpoints[0].url}/user`;
};

export async function check2faSvc(email: string, password: string): Promise<{ is2FAEnabled: boolean }> {
    const body = { password, login: email };
    const response = await httpRequest(`${getApiEndpoint()}/check-2fa`, 'POST', body);
    const json = await response.json();
    if (json.error !== undefined) {
        throw new Error(json.message);
    }
    return json;
}

export async function loginSvc(email: string, password: string, isRememberme = true, otp = null): Promise<any> {
    const body = { password, login: email, isRememberme, otp };
    const response = await httpRequest(`${getApiEndpoint()}/login`, 'POST', body);
    const json = await response.json();
    if (json.error !== undefined) {
        throw new Error(json.message);
    }
    return json;
}

export async function resend2faCodeSvc(email: string, password: string): Promise<boolean> {
    const body = { password, login: email };
    const response = await httpRequest(`${getApiEndpoint()}/resend-2fa`, 'POST', body);
    const json = await response.json();
    if (json.error !== undefined) {
        throw new Error(json.message);
    }
    return response.status === 200;
}

export async function acceptLegalSvc(): Promise<IUser> {
    const response = await httpRequest(`${getApiEndpoint()}/accept-legal`, 'POST');
    const json = await response.json();
    if (json.error !== undefined) {
        throw new Error(json.message);
    }
    return json;
}

export async function logoutSvc(): Promise<void> {
    await httpRequest(`${getApiEndpoint()}/logout`, 'POST');
}

export async function handleResponse(response: Response) {
    const json = await response.json();
    if (json.error) {
        throw json.error;
    }
    if (!response.ok) {
        if (response.status === 401) {
            // auto logout if 401 response returned from api
            logoutSvc();
            location.reload(); // eslint-disable-line no-restricted-globals
        }

        const error = (json && json.message) || response.statusText;
        throw error;
    }

    return json;
}

export async function getUserMeSvc() {
    const response = await httpRequest(`${getApiEndpoint()}/me?z=${Math.random().toString(36).substring(7)}`);
    if (response.status === 204) {
        return null;
    }
    const json = await response.json();
    if (json.error) {
        await Promise.reject(json.error);
    }
    return json;
}

export async function getUserSvc(userId: string) {
    const response = await httpRequest(`${getApiEndpoint()}/${userId}?z=${Math.random().toString(36).substring(7)}`);
    return handleResponse(response);
}

export async function adquireUserSvc(userId: string) {
    const response = await httpRequest(`${getApiEndpoint()}/${userId}/adquire`, 'GET');
    return handleResponse(response);
}

export async function registerUser(user: IUser, recaptcha: string) {
    const response = await httpRequest(`${getApiEndpoint()}/register/new`, 'POST', { ...user, recaptcha });
    const json = await response.json();
    if (json.error !== undefined) {
        await Promise.reject(json.message);
    }
    return json;
}

export async function addUserSvc(user: IUser) {
    const response = await httpRequest(`${getApiEndpoint()}/`, 'POST', { ...user });
    const json = await response.json();
    if (json.error !== undefined) {
        await Promise.reject(json.message);
    }
    return json;
}

export async function resetPassword(email = '') {
    const body = { email: email.trim() };
    const response = await httpRequest(`${getApiEndpoint()}/passwordreset`, 'POST', body);
    return response;
}

export async function changePassword(password, authorization, user) {
    const body = { password, authorization, user };
    await httpRequest(`${getApiEndpoint()}/${user}/passwordreset`, 'POST', body);
}

export async function checkExistingEmail(email: string, skipUid = '') {
    let isExists = false;
    try {
        const response = await httpRequest(`${getApiEndpoint()}/register/checkemail/${email}?skipUid=${skipUid}`);
        if (response.status === 204) {
            return false;
        }
        const json = await response.json();
        isExists = !!json.userId;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
        isExists = true;
    }
    return isExists;
}

export async function checkDNIAvailability(dni: string, skipUid = '') {
    let isExists = false;
    try {
        const response = await httpRequest(`${getApiEndpoint()}/register/checknif/${dni}?skipUid=${skipUid}`);
        if (response.status === 204) {
            return false;
        }
        const json = await response.json();
        isExists = !!json.userId;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
        isExists = true;
    }
    return isExists;
}

export async function checkPhoneAvailability(phoneNumber: string, skipUid = '') {
    let isExists = false;
    try {
        const response = await httpRequest(`${getApiEndpoint()}/register/checkphone/${phoneNumber}?skipUid=${skipUid}`);
        if (response.status === 204) {
            return false;
        }
        const json = await response.json();
        isExists = !!json.userId;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
        isExists = true;
    }
    return isExists;
}

export async function updateUserSvc(userId: string, user: IUser) {
    const response = await httpRequest(`${getApiEndpoint()}/${userId}`, 'PUT', user);
    return handleResponse(response);
}

function transformSearchFilterUser(filter: IUserSearchFilter) {
    const searchField: any = {
        $and: [],
    };

    if (filter.dni) {
        searchField.$and.push({
            'personalData.dni': filter.dni,
        });
    }

    if (filter.email) {
        searchField.$and.push({
            email: filter.email,
        });
    }

    return searchField;
}

export async function getUsersSvc(filter: IUserSearchFilter): Promise<IUser[]> {
    const sFilter = transformSearchFilterUser(filter);

    const hasSearchFilter = (
        filter.dni
        || filter.email
    );

    const filterParams = hasSearchFilter
        ? `search=${encodeURIComponent(JSON.stringify(sFilter))}&`
        : '';

    const response = await httpRequest(`${getApiEndpoint()}?${filterParams}`, 'GET');
    const json = await response.json();
    if (json.error !== undefined) {
        await Promise.reject(json.message);
    }
    return json;
}

function transformSearchFilterProfesional(filter: IProfesionalSearchFilter) {
    const searchField: any = {
        $and: [],
    };

    if (filter.searchString) {
        const regex = buildSearchRegex(filter.searchString);
        searchField.$and.push({
            $or: [
                { 'personalData.nom': { $regex: regex, $options: 'i' } },
                { 'personalData.cognoms': { $regex: regex, $options: 'i' } },
                { email: { $regex: regex, $options: 'i' } },
            ],
        });
    }

    if (filter.roles && filter.roles.length > 0) {
        searchField.$and.push({
            roles: { $in: filter.roles },
        });
    }

    if (filter.estat && filter.estat.length > 0) {
        const booleanEstats = filter.estat.map(estat => parseInt(estat, 10) === 1);
        searchField.$and.push({
            $or: booleanEstats.map(estat => ({ isDeleted: estat })),
        });
    }

    if (filter.email) {
        const regex = buildSearchRegex(filter.email);
        searchField.$and.push({
            email: { $regex: regex, $options: 'i' },
        });
    }

    if (filter.entitats && filter.entitats.length > 0) {
        searchField.$and.push({
            entitat: { $in: filter.entitats },
        });
    }

    if (filter.territoris && filter.territoris.length > 0) {
        searchField.$and.push({
            lote: { $in: filter.territoris },
        });
    }

    if (filter.oficines && filter.oficines.length > 0) {
        searchField.$and.push({
            oficina: { $in: filter.oficines },
        });
    }

    if (filter.nom) {
        const regex = buildSearchRegex(filter.nom);
        searchField.$and.push({
            'personalData.nom': { $regex: regex, $options: 'i' },
        });
    }

    return searchField;
}

export async function getRelatedProfessionalsByFilterSvc(filter: IProfesionalSearchFilter, page: number, pageSize = 20): Promise<{ count:number, data: IUser[] }> {
    const sFilter = transformSearchFilterProfesional(filter);
    const hasSearchFilter = (
        filter.searchString
        || (filter.roles && filter.roles.length > 0)
        || filter.email
        || (filter.entitats && filter.entitats.length > 0)
        || filter.nom
        || (filter.estat && filter.estat.length > 0)
        || (filter.territoris && filter.territoris.length > 0)
        || (filter.oficines && filter.oficines.length > 0)
    );

    const filterParams = hasSearchFilter
        ? `search=${encodeURIComponent(JSON.stringify(sFilter))}&`
        : '';

    const sortParams = filter.sortOrder ? `sort=${filter.sortOrder}&` : '';
    const pageParams = page ? `page=${page}&` : '';
    const pageSizeParams = pageSize ? `pageitems=${pageSize}` : '';

    const response = await httpRequest(`${getApiEndpoint()}/professionals?${sortParams}${filterParams}${pageParams}${pageSizeParams}`, 'GET');
    const json = await response.json();
    if (json.error !== undefined) {
        await Promise.reject(json.message);
    }
    return json;
}

export async function updateLangSvc(lang: string): Promise<IUser> {
    const body = { lang };
    const response = await httpRequest(`${getApiEndpoint()}/lang`, 'PUT', body);
    const json = await response.json();
    if (json.error !== undefined) {
        await Promise.reject(json.message);
    }
    return json;
}

export async function reactivateUserSvc(userId: string): Promise<IUser> {
    const response = await httpRequest(`${getApiEndpoint()}/${userId}/reactivate`, 'POST');
    const json = await response.json();
    if (json.error !== undefined) {
        await Promise.reject(json.message);
    }
    return json;
}

export async function checkExpedientExistingEmail(email: string, skipUid = '') {
    let isExists = false;
    try {
        const response = await httpRequest(`${getApiEndpoint()}/register/checkemail-expedient/${email}?skipUid=${skipUid}`);
        if (response.status === 204) {
            return false;
        }
        const json = await response.json();
        isExists = !!json.hash;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
        isExists = true;
    }
    return isExists;
}

export async function checkExpedientDNIAvailability(dni: string, skipUid = '') {
    let isExists = false;
    try {
        const response = await httpRequest(`${getApiEndpoint()}/register/checknif-expedient/${dni}?skipUid=${skipUid}`);
        if (response.status === 204) {
            return false;
        }
        const json = await response.json();
        isExists = !!json.hash;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
        isExists = true;
    }
    return isExists;
}
